// =====================================================
// FuMind_GM HR Module - CTC Service
// Frontend: React + Power Automate + SharePoint
// Flows:
// - F_HR_CTC_MasterData_Unified
// - F_HR_CTC_Action_Unified
// =====================================================

// Boss: paste the latest flow URLs here after backend correction/testing.
const HR_CTC_MASTERDATA_FLOW_URL = "/tracka-flow/v1/HR_CTC_MASTERDATA";
const HR_CTC_ACTION_FLOW_URL = "/tracka-flow/v1/HR_CTC_ACTION";

// CTC flows were designed with this API key.
// If you changed it in Power Automate, update here also.
const CTC_API_KEY = "";

// =====================================================
// Common Helpers
// =====================================================
async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage = data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();
    throw new Error(
      flowMessage
        ? `${fallbackMessage}: ${flowMessage}`
        : `${fallbackMessage}. HTTP ${statusText}`
    );
  }
}

function ensureMasterDataConfigured() {
  if (!HR_CTC_MASTERDATA_FLOW_URL || HR_CTC_MASTERDATA_FLOW_URL.includes("PASTE_")) {
    throw new Error("CTC MasterData flow URL not configured.");
  }
}

function ensureActionConfigured() {
  if (!HR_CTC_ACTION_FLOW_URL || HR_CTC_ACTION_FLOW_URL.includes("PASTE_")) {
    throw new Error("CTC Action flow URL not configured.");
  }
}

const OPTIONAL_DATE_FIELDS = new Set([
  "EffectiveTo",
  "SubmittedOn",
  "ApprovedOn",
  "RejectedOn",
  "ChangedOn",
  "LastReviewedOn",
  "CTCLastApprovedOn",
]);

const CTC_NUMBER_FIELDS = new Set([
  // HR_CTCComponentCatalogue
  "SerialNumber",
  // HR_CTCStructures
  "DisplayOrder",

  // HR_CTCComponents
  "DefaultPercentage",
  "DefaultAmount",
  "MinimumAmount",
  "MaximumAmount",

  // HR_EmployeeCTC
  "FTEPercent",
  "AnnualCTC",
  "MonthlyCTC",
  "AnnualGross",
  "MonthlyGross",
  "AnnualEmployerCost",
  "MonthlyEmployerCost",
  "AnnualEmployeeDeductions",
  "MonthlyEmployeeDeductions",
  "AnnualVariablePayTarget",
  "HourlyRate",
  "NetPayEstimateMonthly",
  "RevisionNumber",
  // HR_PayScaleBands
  "MinAnnualCTC",
  "MidAnnualCTC",
  "MaxAnnualCTC",
  "MinMonthlyGross",
  "MaxMonthlyGross",
  "DisplayOrder",

  // HR_EmployeeCTCHistory
  "PreviousAnnualCTC",
  "NewAnnualCTC",
  "PreviousMonthlyGross",
  "NewMonthlyGross",
  "PreviousAnnualEmployerCost",
  "NewAnnualEmployerCost",
]);

function normalizePayloadDateValue(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
}

function normalizePayloadNumberValue(value) {
  if (value === undefined) return undefined;
  if (value === null) return 0.0;
  if (typeof value === "string" && value.trim() === "") return 0.0;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0.0;
}

function cleanPayload(payload = {}) {
  const output = {};

  Object.keys(payload || {}).forEach((key) => {
    if (key === "@odata.etag" || key === "odata.etag") return;

    const value = payload[key];

    // Keep false/0 because they are valid values.
    if (value === undefined) return;

    // SharePoint Date/DateTime columns do not accept empty string "".
    if (OPTIONAL_DATE_FIELDS.has(key)) {
      output[key] = normalizePayloadDateValue(value);
      return;
    }

    // SharePoint Number/Currency columns do not accept empty string "".
    // For CTC optional numeric/currency fields, send 0.0 from frontend instead of blank string.
    if (CTC_NUMBER_FIELDS.has(key)) {
      output[key] = normalizePayloadNumberValue(value);
      return;
    }

    output[key] = value;
  });

  return output;
}

function normalizeText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean).join(";");
  if (typeof value === "object") {
    return normalizeText(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  }
  return String(value).trim();
}

function toNumber(value, fallback = 0) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const cleanValue = normalizeText(value).replace(/[^0-9.-]/g, "");
  const numberValue = Number(cleanValue || value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}
function toBoolean(value, fallback = false) {
  if (value === undefined || value === null || value === "") return fallback;
  return normalizeBoolean(value);
}

function normalizeBoolean(value) {
  return value === true || value === "true" || value === "True" || value === "Yes" || value === "yes" || value === 1 || value === "1";
}

function getField(record, ...candidateNames) {
  if (!record || typeof record !== "object") return "";
  for (const name of candidateNames) {
    if (Object.prototype.hasOwnProperty.call(record, name)) return record[name];
  }
  return "";
}

function getArray(data, ...candidateNames) {
  for (const name of candidateNames) {
    const value = getField(data, name);
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // ignore parse failure
      }
    }
  }
  return [];
}

// =====================================================
// Normalizers for CSV-created SharePoint field_number rows
// =====================================================
export function normalizeEmployeeCTC(row = {}) {
  if (!row || typeof row !== "object") return null;
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(row, "ID", "id", "ItemInternalId") || ""),
    title: getField(row, "Title") || "",

    employeeCTCId: getField(row, "EmployeeCTCId", "field_1") || "",
    employeeCode: getField(row, "EmployeeCode", "field_2") || "",
    workEmail: getField(row, "WorkEmail", "field_3") || "",
    employeeName: getField(row, "EmployeeName", "field_4") || "",
    countryCode: normalizeText(getField(row, "CountryCode", "field_5")),
    regionGroup: normalizeText(getField(row, "RegionGroup", "field_6")),
    legalEntityCode: getField(row, "LegalEntityCode", "field_7") || "",
    locationCode: getField(row, "LocationCode", "field_8") || "",
    departmentCode: getField(row, "DepartmentCode", "field_9") || "",
    workUnitCode: getField(row, "WorkUnitCode", "field_10") || "",
    industryType: normalizeText(getField(row, "IndustryType", "field_11")),
    employmentType: normalizeText(getField(row, "EmploymentType", "field_12")),
    workArrangement: normalizeText(getField(row, "WorkArrangement", "field_13")),
    ftePercent: getField(row, "FTEPercent", "field_14") || "",
    payGrade: getField(row, "PayGrade", "field_15") || "",
    ctcStructureId: getField(row, "CTCStructureId", "field_16") || "",
    payFrequency: normalizeText(getField(row, "PayFrequency", "field_17")),
    salaryCurrency: normalizeText(getField(row, "SalaryCurrency", "field_18")),
    annualCTC: getField(row, "AnnualCTC", "field_19") || "",
    monthlyCTC: getField(row, "MonthlyCTC", "field_20") || "",
    annualGross: getField(row, "AnnualGross", "field_21") || "",
    monthlyGross: getField(row, "MonthlyGross", "field_22") || "",
    annualEmployerCost: getField(row, "AnnualEmployerCost", "field_23") || "",
    monthlyEmployerCost: getField(row, "MonthlyEmployerCost", "field_24") || "",
    annualEmployeeDeductions: getField(row, "AnnualEmployeeDeductions", "field_25") || "",
    monthlyEmployeeDeductions: getField(row, "MonthlyEmployeeDeductions", "field_26") || "",
    annualVariablePayTarget: getField(row, "AnnualVariablePayTarget", "field_27") || "",
    hourlyRate: getField(row, "HourlyRate", "field_28") || "",
    netPayEstimateAvailable: normalizeBoolean(getField(row, "NetPayEstimateAvailable", "field_29")),
    netPayEstimateMonthly: getField(row, "NetPayEstimateMonthly", "field_30") || "",
    componentBreakupJson: getField(row, "ComponentBreakupJson", "field_31") || "",
    effectiveFrom: getField(row, "EffectiveFrom", "field_32") || "",
    effectiveTo: getField(row, "EffectiveTo", "field_33") || "",
    ctcStatus: normalizeText(getField(row, "CTCStatus", "field_34")),
    isCurrent: normalizeBoolean(getField(row, "IsCurrent", "field_35")),
    isActive: normalizeBoolean(getField(row, "IsActive", "field_36")),
    approvalRequired: normalizeBoolean(getField(row, "ApprovalRequired", "field_37")),
    submittedBy: getField(row, "SubmittedBy", "field_38") || "",
    submittedOn: getField(row, "SubmittedOn", "field_39") || "",
    approvedBy: getField(row, "ApprovedBy", "field_40") || "",
    approvedOn: getField(row, "ApprovedOn", "field_41") || "",
    rejectedBy: getField(row, "RejectedBy", "field_42") || "",
    rejectedOn: getField(row, "RejectedOn", "field_43") || "",
    rejectionReason: getField(row, "RejectionReason", "field_44") || "",
    revisionNumber: getField(row, "RevisionNumber", "field_45") || "",
    revisionReason: getField(row, "RevisionReason", "field_46") || "",
    previousEmployeeCTCId: getField(row, "PreviousEmployeeCTCId", "field_47") || "",
    sourceModule: normalizeText(getField(row, "SourceModule", "field_48")),
    confidentialityLevel: normalizeText(getField(row, "ConfidentialityLevel", "field_49")),
    remarks: getField(row, "Remarks", "field_50") || "",
    revisionDueFlag: normalizeBoolean(getField(row, "RevisionDueFlag")),
    raw: row,
  };
}

export function normalizeCTCHistory(row = {}) {
  if (!row || typeof row !== "object") return null;
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(row, "ID", "id", "ItemInternalId") || ""),
    title: getField(row, "Title") || "",
    ctcHistoryId: getField(row, "CTCHistoryId", "field_1") || "",
    employeeCTCId: getField(row, "EmployeeCTCId", "field_2") || "",
    employeeCode: getField(row, "EmployeeCode", "field_3") || "",
    workEmail: getField(row, "WorkEmail", "field_4") || "",
    countryCode: normalizeText(getField(row, "CountryCode", "field_5")),
    legalEntityCode: getField(row, "LegalEntityCode", "field_6") || "",
    ctcStructureId: getField(row, "CTCStructureId", "field_7") || "",
    salaryCurrency: normalizeText(getField(row, "SalaryCurrency", "field_8")),
    previousAnnualCTC: getField(row, "PreviousAnnualCTC", "field_9") || "",
    newAnnualCTC: getField(row, "NewAnnualCTC", "field_10") || "",
    previousMonthlyGross: getField(row, "PreviousMonthlyGross", "field_11") || "",
    newMonthlyGross: getField(row, "NewMonthlyGross", "field_12") || "",
    previousAnnualEmployerCost: getField(row, "PreviousAnnualEmployerCost", "field_13") || "",
    newAnnualEmployerCost: getField(row, "NewAnnualEmployerCost", "field_14") || "",
    effectiveFrom: getField(row, "EffectiveFrom", "field_15") || "",
    effectiveTo: getField(row, "EffectiveTo", "field_16") || "",
    revisionNumber: getField(row, "RevisionNumber", "field_17") || "",
    changeType: normalizeText(getField(row, "ChangeType", "field_18")),
    changeReason: getField(row, "ChangeReason", "field_19") || "",
    changedBy: getField(row, "ChangedBy", "field_20") || "",
    changedOn: getField(row, "ChangedOn", "field_21") || "",
    approvedBy: getField(row, "ApprovedBy", "field_22") || "",
    approvedOn: getField(row, "ApprovedOn", "field_23") || "",
    snapshotJson: getField(row, "SnapshotJson", "field_24") || "",
    sourceModule: getField(row, "SourceModule", "field_25") || "",
    isActive: normalizeBoolean(getField(row, "IsActive", "field_26")),
    raw: row,
  };
}


export function normalizeCTCComponentCatalogue(row = {}) {
  if (!row || typeof row !== "object") return null;
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(row, "ID", "id", "ItemInternalId") || ""),
    title: getField(row, "Title") || "",
    catalogueComponentId: getField(row, "CatalogueComponentId", "field_1") || "",
    serialNumber: getField(row, "SerialNumber", "field_2") || "",
    country: getField(row, "Country", "field_3") || "",
    countryCode: normalizeText(getField(row, "CountryCode", "field_4")),
    stateOrRegion: normalizeText(getField(row, "StateOrRegion", "field_5")) || "ALL",
    applicableIndustryTypes: getField(row, "ApplicableIndustryTypes", "field_6") || "All",
    applicableEmploymentTypes: getField(row, "ApplicableEmploymentTypes", "field_7") || "All",
    applicableWorkArrangements: getField(row, "ApplicableWorkArrangements", "field_8") || "All",
    applicabilityType: normalizeText(getField(row, "ApplicabilityType", "field_9")),
    componentCode: getField(row, "ComponentCode", "field_10") || "",
    componentName: getField(row, "ComponentName", "field_11") || "",
    componentCategory: normalizeText(getField(row, "ComponentCategory", "field_12")),
    componentSubCategory: normalizeText(getField(row, "ComponentSubCategory", "field_13")),
    calculationBaseComponentCode: getField(row, "CalculationBaseComponentCode", "field_14") || "",
    calculationMethod: normalizeText(getField(row, "CalculationMethod", "field_15")),
    prorationMethod: normalizeText(getField(row, "ProrationMethod", "field_16")),
    defaultPercentage: getField(row, "DefaultPercentage", "field_17") || "",
    defaultAmount: getField(row, "DefaultAmount", "field_18") || "",
    minimumAmount: getField(row, "MinimumAmount", "field_19") || "",
    maximumAmount: getField(row, "MaximumAmount", "field_20") || "",
    taxabilityRule: normalizeText(getField(row, "TaxabilityRule", "field_21")),
    isTaxable: normalizeText(getField(row, "TaxabilityRule", "field_21")),
    displayGroup: normalizeText(getField(row, "DisplayGroup", "field_22")),
    displayOrder: Number(getField(row, "DisplayOrder", "field_23") || 999),
    hrSelectable: normalizeBoolean(getField(row, "HRSelectable", "field_24")),
    isEmployerCost: normalizeBoolean(getField(row, "IsEmployerCost", "field_25")),
    isEmployeeDeduction: normalizeBoolean(getField(row, "IsEmployeeDeduction", "field_26")),
    isCashComponent: normalizeBoolean(getField(row, "IsCashComponent", "field_27")),
    isVariable: normalizeBoolean(getField(row, "IsVariable", "field_28")),
    isProrated: normalizeBoolean(getField(row, "IsProrated", "field_29")),
    includeInAnnualCTC: normalizeBoolean(getField(row, "IncludeInAnnualCTC", "field_30")),
    includeInMonthlyGross: normalizeBoolean(getField(row, "IncludeInMonthlyGross", "field_31")),
    includeInNetPayEstimate: normalizeBoolean(getField(row, "IncludeInNetPayEstimate", "field_32")),
    requiresPayrollValidation: normalizeBoolean(getField(row, "RequiresPayrollValidation", "field_33")),
    effectiveFrom: getField(row, "EffectiveFrom", "field_34") || "",
    effectiveTo: getField(row, "EffectiveTo", "field_35") || "",
    status: normalizeText(getField(row, "Status", "field_36")),
    isActive: normalizeBoolean(getField(row, "IsActive", "field_37")),
    sourceReference: getField(row, "SourceReference", "field_38") || "",
    notes: getField(row, "Notes", "field_39") || "",
    raw: row,
  };
}

function normalizeCTCComponent(row = {}) {
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(row, "ID", "id", "ItemInternalId") || ""),
    title: getField(row, "Title") || "",
    ctcComponentId: getField(row, "CTCComponentId", "field_1") || "",
    ctcStructureId: getField(row, "CTCStructureId", "field_2") || "",
    componentCode: getField(row, "ComponentCode", "field_3") || "",
    componentName: getField(row, "ComponentName", "field_4") || "",
    countryCode: normalizeText(getField(row, "CountryCode", "field_5")),
    componentCategory: normalizeText(getField(row, "ComponentCategory", "field_6")),
    componentSubCategory: normalizeText(getField(row, "ComponentSubCategory", "field_7")),
    calculationMethod: normalizeText(getField(row, "CalculationMethod", "field_8")),
    defaultPercentage: getField(row, "DefaultPercentage", "field_10") || "",
    defaultAmount: getField(row, "DefaultAmount", "field_11") || "",
    displayOrder: Number(getField(row, "DisplayOrder", "field_26") || 999),
    status: normalizeText(getField(row, "Status", "field_29")),
    isActive: normalizeBoolean(getField(row, "IsActive", "field_30")),
    raw: row,
  };
}

function normalizeCTCStructure(row = {}) {
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    ID: String(getField(row, "ID", "id", "ItemInternalId") || ""),
    title: getField(row, "Title") || "",
    ctcStructureId: getField(row, "CTCStructureId", "field_1") || "",
    structureName: getField(row, "StructureName", "field_2") || "",
    countryCode: normalizeText(getField(row, "CountryCode", "field_3")),
    regionGroup: normalizeText(getField(row, "RegionGroup", "field_4")),
    legalEntityCode: getField(row, "LegalEntityCode", "field_5") || "",
    locationCode: getField(row, "LocationCode", "field_6") || "",
    departmentCode: getField(row, "DepartmentCode", "field_27") || "",
    workUnitCode: getField(row, "WorkUnitCode", "field_28") || "",
    industryType: normalizeText(getField(row, "IndustryType", "field_7")),
    employmentType: normalizeText(getField(row, "EmploymentType", "field_8")),
    workArrangement: normalizeText(getField(row, "WorkArrangement", "field_9")),
    payFrequency: normalizeText(getField(row, "PayFrequency", "field_10")),
    defaultCurrency: normalizeText(getField(row, "DefaultCurrency", "field_11")),
    structureBasis: normalizeText(getField(row, "StructureBasis", "field_12")),
    status: normalizeText(getField(row, "Status", "field_20")),
    isDefault: normalizeBoolean(getField(row, "IsDefault", "field_21")),
    isActive: normalizeBoolean(getField(row, "IsActive", "field_22")),
    displayOrder: Number(getField(row, "DisplayOrder", "field_23") || 999),
    raw: row,
  };
}

// =====================================================
// Flow Call Helpers
// =====================================================


function normalizePayScaleBand(row = {}) {
  if (!row || typeof row !== "object") return null;
  const payGradeCode = normalizeText(getField(row, "PayGradeCode", "payGradeCode", "Title", "field_1"));
  if (!payGradeCode) return null;
  const countryCode = normalizeText(getField(row, "CountryCode", "countryCode", "field_3")) || "ALL";
  const legalEntityCode = normalizeText(getField(row, "LegalEntityCode", "legalEntityCode", "field_4"));
  const industryType = normalizeText(getField(row, "IndustryType", "industryType", "field_5")) || "All";
  const employmentType = normalizeText(getField(row, "EmploymentType", "employmentType", "field_6")) || "All";
  const currency = normalizeText(getField(row, "Currency", "currency", "field_7")) || "INR";
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    payGradeCode,
    gradeName: normalizeText(getField(row, "GradeName", "gradeName", "field_2")) || payGradeCode,
    countryCode,
    legalEntityCode,
    industryType,
    employmentType,
    currency,
    minAnnualCTC: toNumber(getField(row, "MinAnnualCTC", "minAnnualCTC", "field_8"), 0),
    midAnnualCTC: toNumber(getField(row, "MidAnnualCTC", "midAnnualCTC", "field_9"), 0),
    maxAnnualCTC: toNumber(getField(row, "MaxAnnualCTC", "maxAnnualCTC", "field_10"), 0),
    minMonthlyGross: toNumber(getField(row, "MinMonthlyGross", "minMonthlyGross", "field_11"), 0),
    maxMonthlyGross: toNumber(getField(row, "MaxMonthlyGross", "maxMonthlyGross", "field_12"), 0),
    effectiveFrom: normalizeText(getField(row, "EffectiveFrom", "effectiveFrom", "field_13")),
    effectiveTo: normalizeText(getField(row, "EffectiveTo", "effectiveTo", "field_14")),
    approvalRequiredForOutOfBand: toBoolean(getField(row, "ApprovalRequiredForOutOfBand", "approvalRequiredForOutOfBand", "field_16"), true),
    displayOrder: toNumber(getField(row, "DisplayOrder", "displayOrder", "field_17"), 999),
    isActive: toBoolean(getField(row, "IsActive", "isActive", "field_15"), true),
    raw: row,
  };
}

function sameOrAll(value, selected) {
  const item = normalizeText(value).toLowerCase();
  const chosen = normalizeText(selected).toLowerCase();
  return !item || item === "all" || item === "global" || item === "all / global" || !chosen || item === chosen;
}

export function findMatchingPayScaleBand(payScaleBands = [], draft = {}) {
  const payGrade = normalizeText(draft.PayGrade || draft.payGrade);
  if (!payGrade) return null;
  const countryCode = normalizeText(draft.CountryCode || draft.countryCode);
  const legalEntityCode = normalizeText(draft.LegalEntityCode || draft.legalEntityCode);
  const industryType = normalizeText(draft.IndustryType || draft.industryType);
  const employmentType = normalizeText(draft.EmploymentType || draft.employmentType);
  const currency = normalizeText(draft.SalaryCurrency || draft.salaryCurrency);
  return (payScaleBands || [])
    .filter((band) => band && band.isActive !== false)
    .filter((band) => normalizeText(band.payGradeCode).toLowerCase() === payGrade.toLowerCase())
    .filter((band) => sameOrAll(band.countryCode, countryCode))
    .filter((band) => sameOrAll(band.legalEntityCode, legalEntityCode))
    .filter((band) => sameOrAll(band.industryType, industryType))
    .filter((band) => sameOrAll(band.employmentType, employmentType))
    .filter((band) => !currency || !band.currency || normalizeText(band.currency).toLowerCase() === currency.toLowerCase())
    .sort((a, b) => {
      const specificityA = [a.countryCode, a.legalEntityCode, a.industryType, a.employmentType].filter((v) => normalizeText(v) && normalizeText(v).toLowerCase() !== "all").length;
      const specificityB = [b.countryCode, b.legalEntityCode, b.industryType, b.employmentType].filter((v) => normalizeText(v) && normalizeText(v).toLowerCase() !== "all").length;
      return specificityB - specificityA || (a.displayOrder || 999) - (b.displayOrder || 999);
    })[0] || null;
}

export function validateEmployeeCTCAgainstPayScale(draft = {}, payScaleBands = []) {
  const payGrade = normalizeText(draft.PayGrade || draft.payGrade);
  if (!payGrade) {
    return { valid: true, severity: "info", message: "PayGrade is blank; pay scale validation skipped.", band: null };
  }
  const band = findMatchingPayScaleBand(payScaleBands, draft);
  if (!band) {
    return { valid: false, severity: "error", message: `No active pay scale band found for PayGrade ${payGrade}.`, band: null };
  }
  const annualCTC = toNumber(draft.AnnualCTC || draft.annualCTC, 0);
  const monthlyGross = toNumber(draft.MonthlyGross || draft.monthlyGross, 0);
  const annualOk = annualCTC >= band.minAnnualCTC && annualCTC <= band.maxAnnualCTC;
  const monthlyOk = !band.minMonthlyGross && !band.maxMonthlyGross ? true : monthlyGross >= band.minMonthlyGross && monthlyGross <= band.maxMonthlyGross;
  if (annualOk && monthlyOk) {
    return { valid: true, severity: "success", message: `Pay scale OK for ${band.payGradeCode}: Annual CTC is within ${band.minAnnualCTC} - ${band.maxAnnualCTC}.`, band };
  }
  const message = `Pay scale mismatch for ${band.payGradeCode}. Annual CTC ${annualCTC} must be between ${band.minAnnualCTC} and ${band.maxAnnualCTC}${band.minMonthlyGross || band.maxMonthlyGross ? `; Monthly Gross ${monthlyGross} must be between ${band.minMonthlyGross} and ${band.maxMonthlyGross}` : ""}.`;
  return { valid: !band.approvalRequiredForOutOfBand, severity: band.approvalRequiredForOutOfBand ? "error" : "warning", message, band };
}

async function callCTCMasterDataFlow({ action, entityKey = "", filters = {}, context = {} } = {}) {
  ensureMasterDataConfigured();
  const requestBody = {

    action,
    entityKey,
    filters,
    context: {
      userEmail: context.userEmail || "",
      userRole: context.userRole || "HRAdmin",
      sourceModule: context.sourceModule || "CTCConsole",
      correlationId: context.correlationId || "",
    },
  };
  const response = await fetch(HR_CTC_MASTERDATA_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, `CTC master-data action ${action} failed`);
  return data;
}

async function callCTCActionFlow({ action, entityKey = "employeeCTC", id = "", payload = {}, changedBy = "", changeReason = "", context = {} } = {}) {
  ensureActionConfigured();
  const requestBody = {

    action,
    entityKey,
    id: String(id || payload?.id || payload?.ID || ""),
    payload: cleanPayload(payload),
    changedBy,
    changeReason,
    sourceModule: "CTCConsole",
    context: {
      userEmail: context.userEmail || changedBy || "",
      userRole: context.userRole || "HRAdmin",
      sourceModule: context.sourceModule || "CTCConsole",
      correlationId: context.correlationId || "",
    },
  };
  const response = await fetch(HR_CTC_ACTION_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, `CTC action ${action} failed`);
  return data;
}

// =====================================================
// MasterData Flow Exports
// =====================================================
export async function loadCTCMasterData(filters = {}, context = {}) {
  const data = await callCTCMasterDataFlow({
    action: "loadMasterData",
    entityKey: "ctcStructures",
    filters,
    context,
  });
  return {
    ...data,
    ctcStructures: getArray(data, "ctcStructures", "CTCStructures").map(normalizeCTCStructure),
    ctcComponents: getArray(data, "ctcComponents", "CTCComponents").map(normalizeCTCComponent),
  };
}

export async function getCurrentEmployeeCTC(employeeCode, context = {}) {
  if (!employeeCode) return null;
  const data = await callCTCMasterDataFlow({
    action: "getCurrent",
    entityKey: "employeeCTC",
    filters: { employeeCode },
    context,
  });
  return data?.data ? normalizeEmployeeCTC(data.data) : null;
}
export async function getEmployeeCTCDraft(employeeCode, context = {}) {
  if (!employeeCode) return null;
  const data = await callCTCMasterDataFlow({
    action: "getDraft",
    entityKey: "employeeCTC",
    filters: { employeeCode },
    context,
  });
  return data?.data ? normalizeEmployeeCTC(data.data) : null;
}
export async function getEmployeeCTCDrafts(filters = {}, context = {}) {
  const data = await callCTCMasterDataFlow({
    action: "getDrafts",
    entityKey: "employeeCTC",
    filters,
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return rows.map(normalizeEmployeeCTC).filter(Boolean);
}

export async function getEmployeeCTCHistory(employeeCode, context = {}) {
  if (!employeeCode) return [];
  const data = await callCTCMasterDataFlow({
    action: "getHistory",
    entityKey: "employeeCTCHistory",
    filters: { employeeCode },
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return rows.map(normalizeCTCHistory).filter(Boolean);
}

export async function getPendingCTCApprovals(filters = {}, context = {}) {
  const data = await callCTCMasterDataFlow({
    action: "getPendingApprovals",
    entityKey: "employeeCTC",
    filters,
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return rows.map(normalizeEmployeeCTC).filter(Boolean);
}



export async function getPayScaleBands(filters = {}, context = {}) {
  const data = await callCTCMasterDataFlow({
    action: "getPayScaleBands",
    entityKey: "payScaleBands",
    filters,
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : getArray(data, "payScaleBands", "HR_PayScaleBands", "PayScaleBands");
  return rows.map(normalizePayScaleBand).filter(Boolean).sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
}

export async function getComponentCatalogue(filters = {}, context = {}) {
  const data = await callCTCMasterDataFlow({
    action: "getComponentCatalogue",
    entityKey: "ctcComponentCatalogue",
    filters,
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : getArray(data, "componentCatalogue", "ctcComponentCatalogue", "CTCComponentCatalogue");
  return rows.map(normalizeCTCComponentCatalogue).filter(Boolean).sort((a, b) => a.displayOrder - b.displayOrder);
}

export async function getCTCStructureComponents(ctcStructureId, context = {}) {
  if (!ctcStructureId) return [];
  const data = await callCTCMasterDataFlow({
    action: "getStructureComponents",
    entityKey: "ctcComponents",
    filters: { ctcStructureId },
    context,
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  return rows.map(normalizeCTCComponent).filter(Boolean).sort((a, b) => a.displayOrder - b.displayOrder);
}

export async function runCTCDataQualityCheck(filters = {}, context = {}) {
  return callCTCMasterDataFlow({
    action: "dataQualityCheck",
    entityKey: "employeeCTC",
    filters,
    context,
  });
}

// =====================================================
// Action Flow Exports
// =====================================================
export async function savePayScaleBand(payload = {}, options = {}) {
  return callCTCActionFlow({
    action: "savePayScaleBand",
    entityKey: "payScaleBands",
    payload,
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Pay scale band saved from Compensation setup",
    context: options.context || {},
  });
}

export async function saveCTCStructure(payload = {}, options = {}) {
  return callCTCActionFlow({
    action: "saveStructure",
    entityKey: "ctcStructures",
    payload,
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "",
    context: options.context || {},
  });
}

export async function saveCTCComponent(payload = {}, options = {}) {
  return callCTCActionFlow({
    action: "saveComponent",
    entityKey: "ctcComponents",
    payload,
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "",
    context: options.context || {},
  });
}

export async function saveEmployeeCTCDraft(payload = {}, options = {}) {
  return callCTCActionFlow({
    action: "saveEmployeeCTCDraft",
    entityKey: "employeeCTC",
    payload,
    changedBy: options.changedBy || payload?.WorkEmail || "",
    changeReason: options.changeReason || payload?.RevisionReason || "CTC draft saved",
    context: options.context || {},
  });
}

export async function submitEmployeeCTC(employeeCTCId, payload = {}, options = {}) {
  if (!employeeCTCId && !payload?.EmployeeCTCId) {
    throw new Error("EmployeeCTCId is required for submit.");
  }
  return callCTCActionFlow({
    action: "submitEmployeeCTC",
    entityKey: "employeeCTC",
    payload: { ...payload, EmployeeCTCId: employeeCTCId || payload.EmployeeCTCId },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Submitted for approval",
    context: options.context || {},
  });
}

export async function approveEmployeeCTC(employeeCTCId, payload = {}, options = {}) {
  if (!employeeCTCId && !payload?.EmployeeCTCId) {
    throw new Error("EmployeeCTCId is required for approval.");
  }
  return callCTCActionFlow({
    action: "approveEmployeeCTC",
    entityKey: "employeeCTC",
    payload: { ...payload, EmployeeCTCId: employeeCTCId || payload.EmployeeCTCId },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Approved",
    context: options.context || {},
  });
}

export async function rejectEmployeeCTC(employeeCTCId, rejectionReason, payload = {}, options = {}) {
  if (!employeeCTCId && !payload?.EmployeeCTCId) {
    throw new Error("EmployeeCTCId is required for rejection.");
  }
  if (!String(rejectionReason || payload?.RejectionReason || "").trim()) {
    throw new Error("Rejection reason is required.");
  }
  return callCTCActionFlow({
    action: "rejectEmployeeCTC",
    entityKey: "employeeCTC",
    payload: {
      ...payload,
      EmployeeCTCId: employeeCTCId || payload.EmployeeCTCId,
      RejectionReason: rejectionReason || payload.RejectionReason,
    },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || rejectionReason || "Rejected",
    context: options.context || {},
  });
}

export async function sendBackEmployeeCTC(employeeCTCId, correctionReason, payload = {}, options = {}) {
  if (!employeeCTCId && !payload?.EmployeeCTCId) {
    throw new Error("EmployeeCTCId is required for send back for correction.");
  }
  if (!String(correctionReason || payload?.CorrectionReason || "").trim()) {
    throw new Error("Correction reason is required for send back for correction.");
  }
  return callCTCActionFlow({
    action: "sendBackEmployeeCTC",
    entityKey: "employeeCTC",
    payload: {
      ...payload,
      EmployeeCTCId: employeeCTCId || payload.EmployeeCTCId,
      CorrectionReason: correctionReason || payload.CorrectionReason,
    },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || correctionReason || "Sent back for correction",
    context: options.context || {},
  });
}

export async function cancelCTCDraft(employeeCTCId, payload = {}, options = {}) {
  if (!employeeCTCId && !payload?.EmployeeCTCId) {
    throw new Error("EmployeeCTCId is required for cancel draft.");
  }
  return callCTCActionFlow({
    action: "cancelDraft",
    entityKey: "employeeCTC",
    payload: { ...payload, EmployeeCTCId: employeeCTCId || payload.EmployeeCTCId },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Draft cancelled",
    context: options.context || {},
  });
}

export async function markCTCRevisionDue(employeeCode, payload = {}, options = {}) {
  if (!employeeCode && !payload?.EmployeeCode) {
    throw new Error("EmployeeCode is required for mark revision due.");
  }
  return callCTCActionFlow({
    action: "markRevisionDue",
    entityKey: "employeeCTC",
    payload: { ...payload, EmployeeCode: employeeCode || payload.EmployeeCode },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Marked revision due",
    context: options.context || {},
  });
}

export async function syncEmployeeMasterCTC(employeeCode, payload = {}, options = {}) {
  if (!employeeCode && !payload?.EmployeeCode) {
    throw new Error("EmployeeCode is required for EmployeeMaster CTC sync.");
  }
  return callCTCActionFlow({
    action: "syncEmployeeMasterCTC",
    entityKey: "employeeCTC",
    payload: { ...payload, EmployeeCode: employeeCode || payload.EmployeeCode },
    changedBy: options.changedBy || "",
    changeReason: options.changeReason || "Manual CTC summary sync",
    context: options.context || {},
  });
}

export default {
  loadCTCMasterData,
  getCurrentEmployeeCTC,
  getEmployeeCTCDraft,
  getEmployeeCTCDrafts,
  getEmployeeCTCHistory,
  getPendingCTCApprovals,
  getCTCStructureComponents,
  getPayScaleBands,
  getComponentCatalogue,
  runCTCDataQualityCheck,
  savePayScaleBand,
  saveCTCStructure,
  saveCTCComponent,
  saveEmployeeCTCDraft,
  validateEmployeeCTCAgainstPayScale,
  findMatchingPayScaleBand,
  submitEmployeeCTC,
  approveEmployeeCTC,
  rejectEmployeeCTC,
  sendBackEmployeeCTC,
  cancelCTCDraft,
  markCTCRevisionDue,
  syncEmployeeMasterCTC,
};
